<script lang="ts">
  import Modal from '../../Modal.svelte';
  import Button from '../../Button.svelte';
  import FormInput from '../../form/FormInput.svelte';
  import { CreateRedirect, Redirect } from '../../../utils/redirect.service.js';
  import { toInputDateString } from '../../../utils/date.js';
  import SelectWithLabel from '../../form/SelectWithLabel.svelte';

  const TAG_MAX_LENGTH = 30;

  export let isOpen: boolean;
  export let redirect: Redirect | undefined = undefined;
  export let onCreateRedirect: (redirect: CreateRedirect) => void;
  export let onUpdateRedirect: (redirect: Redirect) => void;

  let source = '';
  let destination = '';
  let redirect_type: string;
  let expirationDate: string | undefined;
  let today = toInputDateString(new Date());
  let canSubmit = false;
  let tag = '';

  const resetForm = () => {
    source = '';
    destination = '';
    expirationDate = '';
    redirect_type = '301';
    redirect = undefined;
    tag = '';
  };

  const apply = async () => {
    if (redirect) {
      onUpdateRedirect({
        ...redirect,
        source,
        destination,
        expiration_date: expirationDate || undefined,
        redirect_type,
        tag: tag?.toLocaleLowerCase().trim()
      });
    } else {
      onCreateRedirect({
        source,
        destination,
        expiration_date: expirationDate || undefined,
        redirect_type,
        tag: tag?.toLocaleLowerCase().trim()
      });
    }
  };

  const cancel = () => {
    isOpen = false;
  };

  const onRedirectChange = () => {
    source = redirect ? redirect.source : '';
    destination = redirect ? redirect.destination : '';
    expirationDate = redirect?.expiration_date
      ? toInputDateString(new Date(redirect.expiration_date))
      : undefined;
    redirect_type = redirect ? String(redirect.redirect_type) : '301';
    tag = redirect?.tag || '';
  };

  const checkIsValid = () => {
    canSubmit = false;

    if (source && destination) {
      if (
        redirect &&
        source === redirect.source &&
        destination === redirect.destination &&
        (expirationDate === redirect.expiration_date ||
          (!expirationDate && !redirect.expiration_date)) &&
        redirect_type === String(redirect.redirect_type) &&
        tag === redirect.tag
      ) {
        canSubmit = false;
        return;
      }

      canSubmit = true;
    }
  };

  $: redirect, onRedirectChange();
  $: source, destination, expirationDate, redirect_type, tag, checkIsValid();
</script>

<Modal
  label={redirect ? 'Weiterleitung bearbeiten' : 'Weiterleitung hinzufügen'}
  bind:isOpen
  onClose={resetForm}
>
  <FormInput
    label="Quelle"
    description="Quell-URL - diese URL soll umgeleitet werden"
    bind:value={source}
    dataTestid="source"
    showEncodeButton
  />
  <FormInput
    label="Ziel"
    description="Ziel-URL - auf diese URL soll weitergeleitet werden"
    bind:value={destination}
    dataTestid="destination"
    showEncodeButton
  />
  <FormInput
    label="Ablaufdatum"
    description="Soll der Redirect nach einem definierten Datum ablaufen? Wenn nicht, kann das Feld leer gelassen werden (default)"
    bind:value={expirationDate}
    type="date"
    min={today}
  />

  <SelectWithLabel
    label="Typ:"
    description="Soll die Weiterleitung dauerhaft (301) für z.B. URL-Änderungen oder nur vorübergehend (302) für z.B. kurze Links sein?"
    labelOnTop
    options={[
      { label: 'Permanent (301)', value: '301' },
      { label: 'Temporär (302)', value: '302' }
    ]}
    bind:selectedOption={redirect_type}
  />

  <div class="tag-input">
    <FormInput
      label="Tag"
      bind:value={tag}
      dataTestid="tag"
      maxlength={TAG_MAX_LENGTH}
      description="Wenn im Zuge einer Migration mehrere Weiterleitungen erstellt werden, sollten Tags hinzugefügt werden, um zusammengehörige Weiterleitungen schnell finden zu können. Einzelne Weiterleitungen müssen nicht getaggt werden."
    />
  </div>

  <svelte:fragment slot="button">
    <Button on:click={cancel}>Abbrechen</Button>
    <Button on:click={apply} disabled={!canSubmit} variant="primary" dataTestid={'apply'}
      >{redirect ? 'Änderungen übernehmen' : 'Weiterleitung erstellen'}</Button
    >
  </svelte:fragment>
</Modal>

<style lang="scss">
  .tag-input :global(input) {
    text-transform: lowercase;
  }
</style>
